import { UMBRELLA_PAYE_HYBRID } from '@nxsys/constants/AppConst';
import { defaultPaymentFrequency } from '@nxsys/constants/AppConstants';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { TypedMessages } from '@nxsys/hooks/useTypedIntl';
import { Icons } from '@nxsys/types/icons';
import { PayrollCountryType } from '@nxsys/types/models/Common';
import { IEmploymentIntermediary } from '@nxsys/types/models/employee';
import { formatFullName, showCurrencyByCountry } from '@nxsys/utils';
import { LoadingOutlined } from '@ant-design/icons';
import { getFieldLabelByCountry } from '@nxsys/utils/labelConfig';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { find, includes } from 'lodash';
import moment from 'moment';
import {
  addTwoStringNumber,
  renderCategories,
} from './employmentIntermediaryUtils';

export const getCISStateOfEarningsColumns = ({
  messages,
  payrollCountry,
  handleSendMail,
  handleExport,
  downloadLoadingId,
}: {
  messages: TypedMessages;
  payrollCountry: PayrollCountryType;
  handleSendMail: (item: IEarningReport) => void;
  handleExport: (item: IEarningReport) => void;
  downloadLoadingId?: string | null;
}): ColumnsType<IEarningReport> => {
  return [
    {
      key: 'actions',
      title: 'Actions',
      fixed: 'left',
      align: 'left',
      render: (record) => {
        const isSendFailed =
          !record.isSentMailEarningReport &&
          record.messageSentMailEarningReport;
        const isSendSuccess = !record.isSentMailEarningReport;

        return (
          <ul className="flex items-center justify-center gap-x-4">
            <li
              className={
                downloadLoadingId === record.id
                  ? 'pointer-events-none text-primary-500'
                  : ''
              }
              onClick={() => handleExport(record)}
            >
              <Tooltip
                title={
                  downloadLoadingId === record.id ? 'Loading...' : 'Download'
                }
              >
                <span>
                  {downloadLoadingId === record.id ? (
                    <LoadingOutlined style={{ fontSize: 16 }} spin />
                  ) : (
                    <Icons.IconDownload />
                  )}
                </span>
              </Tooltip>
            </li>
            <li>
              <Tooltip
                title={
                  record?.messageSentMailEarningReport ||
                  messages['common.emailCISStatementOfEarnings']
                }
              >
                <span onClick={() => handleSendMail(record)}>
                  {isSendFailed ? (
                    <Icons.IconSendEmailFailed />
                  ) : isSendSuccess ? (
                    <Icons.IconEmail
                      color={defaultTheme.theme.palette.green[500]}
                    />
                  ) : (
                    <Icons.IconEmail
                      color={defaultTheme.theme.palette.gray[500]}
                    />
                  )}
                </span>
              </Tooltip>
            </li>
          </ul>
        );
      },
      width: 100,
    },
    {
      key: 'taxPeriod',
      title: messages['common.taxPeriod'],
      align: 'right',
      dataIndex: 'taxPeriod',
    },
    {
      key: 'taxYear',
      title: messages['common.taxYear'],
      align: 'right',
      render: (record) => record?.taxYear || record?.selfBillRef?.taxYear || '',
    },
    {
      key: 'name',
      title: messages['common.name'],
      render: (_value, record) => formatFullName(record) || '_',
    },
    {
      key: 'UTRNo',
      title: messages['common.utrNo'],
      dataIndex: 'UTRNo',
      // sorter: true,
      // sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'NINumber',
      title: getFieldLabelByCountry(payrollCountry ?? '', 'NINo'),
      dataIndex: 'NINumber',
      // sorter: true,
      // sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'totalGross',
      title: messages['common.grossAmount'],
      align: 'right',
      dataIndex: 'totalGross',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (props) => (
        <span>{formatMoney(props, showCurrencyByCountry(payrollCountry))}</span>
      ),
    },
    {
      key: 'totalCisDeduction',
      title: messages['common.cisTaxDeduction'],
      align: 'right',
      dataIndex: 'totalCisDeduction',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (props) => (
        <span>{formatMoney(props, showCurrencyByCountry(payrollCountry))}</span>
      ),
    },
    {
      key: 'totalMaterials',
      title: messages['common.material'],
      align: 'right',
      dataIndex: 'totalMaterials',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (props) => (
        <span>{formatMoney(props, showCurrencyByCountry(payrollCountry))}</span>
      ),
    },
    {
      key: 'totalOtherDeduction',
      title: messages['common.otherDeduction'],
      align: 'right',
      dataIndex: 'totalOtherDeduction',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (props) => (
        <span>{formatMoney(props, showCurrencyByCountry(payrollCountry))}</span>
      ),
    },
    {
      key: 'totalVat',
      title: messages['common.vatPaid'],
      align: 'right',
      dataIndex: 'totalVat',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (props) => (
        <span>{formatMoney(props, showCurrencyByCountry(payrollCountry))}</span>
      ),
    },
    {
      key: 'totalNet',
      title: messages['common.netAmount'],
      align: 'right',
      dataIndex: 'totalNet',
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (props) => (
        <span>{formatMoney(props, showCurrencyByCountry(payrollCountry))}</span>
      ),
    },
  ];
};

export const getDormantWorkerColumns = ({
  messages,
  isBureau,
}: {
  messages: TypedMessages;
  isBureau?: boolean;
}): ColumnsType<IDormantWorker> => {
  return [
    {
      key: 'id',
      title: messages['common.employeeId'],
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      width: 240,
      render: (record) => record?.id || '_',
    },
    {
      key: 'employeeName',
      title: messages['common.name'],
      width: 240,
      render: (record) => formatFullName(record) || '_',
    },
    {
      key: 'agencyRef',
      title: isBureau
        ? messages['table.employerRef']
        : messages['common.agencyRef'],
      width: 240,
      render: (record) =>
        `${record?.primaryAgency?.agencyPrefix || '___'}/${
          record?.agencyRef || record?.id || '_'
        }`,
    },
    ...(!isBureau
      ? [
          {
            key: 'agencyName',
            title: messages['common.agencyName'],
            width: 350,
            render: (record: IDormantWorker) =>
              record?.primaryAgency?.name || '_',
          },
        ]
      : []),
    {
      key: 'payFrequency',
      title: messages['common.payFrequency'],
      width: 240,
      render: (record) =>
        find(
          defaultPaymentFrequency,
          (item) => item?.value === record.payFrequency,
        )?.label || '_',
    },
    {
      key: 'paymentCompany',
      title: isBureau
        ? messages['common.employer']
        : messages['common.paymentCompany'],
      width: 350,
      render: (record) => record?.companyPayment?.companyName || '_',
    },
    {
      key: 'department',
      title: messages['common.department'],
      width: 240,
      render: (record) => record?.department || '_',
    },
    {
      key: 'dateOfJoining',
      title: messages['common.dateOfJoining'],
      width: 240,
      render: (record) =>
        record?.dateOfJoining
          ? moment(record?.dateOfJoining)
              .tz('Europe/London')
              .format('DD/MM/YYYY')
          : '_',
    },
    {
      key: 'periodLastPaid',
      title: messages['common.periodLastPaid'],
      width: 240,
      render: (record) =>
        record?.previousPayroll
          ? `${record?.previousPayroll?.taxPeriod || ''}/${
              record?.previousPayroll?.taxYear ||
              record?.previousPayroll?.selfBillRef?.taxYear ||
              ''
            }`
          : '_',
    },
    {
      key: 'lastPaidDate',
      title: messages['common.dateLastPaid'],
      width: 240,
      sorter: true,
      sortDirections: ['descend', 'ascend'],
      render: (record) =>
        record?.lastPaidDate
          ? moment(record?.lastPaidDate)
              .tz('Europe/London')
              .format('DD/MM/YYYY')
          : '_',
    },
  ];
};

export const getEmploymentIntermediaryColumns = ({
  messages,
}: {
  messages: TypedMessages;
}): ColumnsType<IEmploymentIntermediary> => {
  return [
    {
      title: messages['common.name'],
      key: 'name',
      sortDirections: ['descend', 'ascend'],
      render: (_value, record) =>
        formatFullName({
          forename: record?.forename,
          surname: record?.surname,
        }) || '',
    },

    {
      key: 'DOB',
      title: messages['report.employmentIntermediary.dob'],
      dataIndex: 'DOB',
      align: 'right',
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '',
    },

    {
      title: messages['report.employmentIntermediary.gender'],
      sorter: true,
      align: 'center',
      dataIndex: 'gender',
      render: (value) =>
        value === 'MALE' ? 'M' : value === 'FEMALE' ? 'F' : 'O',
    },

    {
      title: messages['report.employmentIntermediary.niNumber'],
      sortDirections: ['descend', 'ascend'],
      dataIndex: 'NINumber',
    },

    {
      key: 'address',
      title: messages['report.employmentIntermediary.addressLine1'],
      dataIndex: 'address',
      render: (value) => (value?.length ? value[0] || '' : ''),
    },
    {
      key: 'address',
      title: messages['report.employmentIntermediary.addressLine2'],
      dataIndex: 'address',
      render: (value) => (value?.length ? value[1] || '' : ''),
    },
    {
      key: 'address',
      title: messages['report.employmentIntermediary.addressLine3'],
      render: (value) => (value?.length ? value[2] || '' : ''),
      dataIndex: 'address',
    },
    {
      key: 'address',
      title: messages['report.employmentIntermediary.addressLine4'],
      render: (value) => (value?.length ? value[3] || '' : ''),
      dataIndex: 'address',
    },
    {
      key: 'postCode',
      title: messages['report.employmentIntermediary.workerPostcode'],
      dataIndex: 'postCode',
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'businessType',
      title: messages['report.employmentIntermediary.engagementDetails'],
      // dataIndex: 'businessType',
      sortDirections: ['descend', 'ascend'],
      render: (value) => {
        const { businessType, paymentModel } = value;
        if (includes(UMBRELLA_PAYE_HYBRID, paymentModel)) return 'F';
        if (businessType) return renderCategories(value);
        return '';
      },
    },

    {
      title: messages['report.employmentIntermediary.utr'],
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        value?.UTRNo &&
        value?.businessType &&
        includes(['A', 'B', 'C'], renderCategories(value?.businessType))
          ? value?.UTRNo
          : '',
    },
    {
      title: messages['report.employmentIntermediary.startDate'],
      align: 'right',
      sortDirections: ['descend', 'ascend'],
      dataIndex: 'startDate',
      render: (value) =>
        value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '',
    },
    {
      title: messages['report.employmentIntermediary.endDate'],
      align: 'right',
      dataIndex: 'dateLeft',
      sortDirections: ['descend', 'ascend'],
      render: (value) =>
        value ? moment(value).tz('Europe/London').format('DD/MM/YYYY') : '',
    },
    {
      title: messages['report.employmentIntermediary.amountPaid'],
      align: 'right',
      sortDirections: ['descend', 'ascend'],
      render: (value) => {
        const { businessType, paymentModel, VATRegNo, totalGross, totalVat } =
          value;
        if (includes(UMBRELLA_PAYE_HYBRID, paymentModel)) {
          if (VATRegNo)
            return addTwoStringNumber(totalGross, totalVat).toFixed(2);
          else return totalGross;
        } else if (
          businessType &&
          includes(['A', 'B', 'C', 'D', 'E'], renderCategories(businessType))
        ) {
          if (VATRegNo)
            return addTwoStringNumber(totalGross, totalVat).toFixed(2);
          else return totalGross;
        }
        return '';
      },
    },
    {
      title: messages['report.employmentIntermediary.currency'],
      sortDirections: ['descend', 'ascend'],
      render: () => 'GBP',
    },
    {
      title: messages['report.employmentIntermediary.amountInclusiveVat'],
      sortDirections: ['descend', 'ascend'],
      // dataIndex: 'VATRegNo',
      render: (value) => {
        const { VATRegNo } = value;
        if (VATRegNo) return 'Y';
        return 'N';
      },
    },
    {
      title: messages['report.employmentIntermediary.partyName'],
      sortDirections: ['descend', 'ascend'],
      render: (value) => {
        const { paymentModel, company_companyName, businessType } = value;
        if (includes(UMBRELLA_PAYE_HYBRID, paymentModel))
          return company_companyName || '';
        else if (
          businessType &&
          includes(['A', 'B', 'C', 'D', 'E'], renderCategories(businessType))
        )
          return company_companyName || '';
        return '';
      },
    },
    {
      title: messages['report.employmentIntermediary.partyAddressLine1'],
      sortDirections: ['descend', 'ascend'],
      render: (value) => {
        const { paymentModel, company_address, businessType } = value;
        if (!company_address?.length) return '';
        else if (includes(UMBRELLA_PAYE_HYBRID, paymentModel))
          return company_address[0];
        else if (
          businessType &&
          includes(['A', 'B', 'C', 'D', 'E'], renderCategories(businessType))
        )
          return company_address[0];
        return '';
      },
    },
    {
      title: messages['report.employmentIntermediary.partyAddressLine2'],
      sortDirections: ['descend', 'ascend'],
      render: (value) => {
        const { paymentModel, company_address, businessType } = value;
        if (!company_address?.length) return '';
        else if (includes(UMBRELLA_PAYE_HYBRID, paymentModel))
          return company_address[1];
        else if (
          businessType &&
          includes(['A', 'B', 'C', 'D', 'E'], renderCategories(businessType))
        )
          return company_address[1];
        return '';
      },
    },
    {
      title: messages['report.employmentIntermediary.partyAddressLine3'],
      sortDirections: ['descend', 'ascend'],
      render: (value) => {
        const { paymentModel, company_address, businessType } = value;
        if (!company_address?.length) return '';
        else if (includes(UMBRELLA_PAYE_HYBRID, paymentModel))
          return company_address[2];
        else if (
          businessType &&
          includes(['A', 'B', 'C', 'D', 'E'], renderCategories(businessType))
        )
          return company_address[2];
        return '';
      },
    },
    {
      title: messages['report.employmentIntermediary.partyAddressLine4'],
      sortDirections: ['descend', 'ascend'],
      render: (value) => {
        const { paymentModel, company_address, businessType } = value;
        if (!company_address?.length) return '';
        else if (includes(UMBRELLA_PAYE_HYBRID, paymentModel))
          return company_address[3];
        else if (
          businessType &&
          includes(['A', 'B', 'C', 'D', 'E'], renderCategories(businessType))
        )
          return company_address[3];
        return '';
      },
    },
    {
      title: messages['report.employmentIntermediary.partyPostcode'],
      sortDirections: ['descend', 'ascend'],
      render: (value) => {
        const { paymentModel, company_postCode, businessType } = value;
        if (!company_postCode) return '';
        else if (includes(UMBRELLA_PAYE_HYBRID, paymentModel))
          return company_postCode;
        else if (
          businessType &&
          includes(['A', 'B', 'C', 'D', 'E'], renderCategories(businessType))
        )
          return company_postCode;
        return '';
      },
    },
    {
      title: messages['report.employmentIntermediary.partyCompanyNumber'],
      sortDirections: ['descend', 'ascend'],
      render: (value) => {
        const { paymentModel, company_companyNo, businessType } = value;
        if (!company_companyNo) return '';
        if (includes(UMBRELLA_PAYE_HYBRID, paymentModel))
          return company_companyNo;
        else if (
          businessType &&
          includes(['A', 'B', 'C', 'D', 'E'], renderCategories(businessType))
        )
          return company_companyNo;
        return '';
      },
    },
  ];
};
